import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { getDefaultAxiosConfig } from "../../../utilities/requests";
import {
  API_get_actual_schedule,
  API_get_actual_used_schedule,
  API_get_calculated_earned_labor,
  API_get_actual_used_labor_vs_earned,
  API_get_actual_intraday_metrics,
  API_get_forecasted_labor,
  API_get_intraday_metrics,
  API_get_store_team_member_weekly_info,
  API_refresh_live_day_pos_data,
  API_get_live_labor_violations,
  API_post_missed_shift,
  API_get_live_alert_config,
} from "../../../constants";
import { postRightworkAPI, getRightworkAPI } from "../requests/utilities/requests";
import { LiveDayLaborReportContent } from "../home/schedule_components/LiveDayLaborReportModal";
import LiveActionTasksPanel from "./LiveActionTasksPanel";
import LoadSpinner from "../../../utilities/LoadSpinner";
import { getStoreNow } from "../../../utilities/storeTime";

const REFRESH_INTERVAL_MINUTES = 15;
const REFRESH_INTERVAL_MS = REFRESH_INTERVAL_MINUTES * 60 * 1000;
const POS_SYNC_INITIAL_POLL_DELAY_MS = 2000;
const POS_SYNC_MAX_POLL_DELAY_MS = 30000;

const wait = (delayMs) =>
  new Promise((resolve) => setTimeout(resolve, delayMs));

const isTransientPosSyncError = (error) =>
  !error?.response ||
  error.response.status === 429 ||
  error.response.status >= 500;

const getCurrentQuarterBoundaryMs = (ts = Date.now()) => {
  const date = new Date(ts);
  date.setSeconds(0, 0);
  date.setMinutes(date.getMinutes() - (date.getMinutes() % REFRESH_INTERVAL_MINUTES));
  return date.getTime();
};

const getMsUntilNextQuarterBoundary = (ts = Date.now()) => {
  const nextBoundaryMs = getCurrentQuarterBoundaryMs(ts) + REFRESH_INTERVAL_MS;
  return Math.max(0, nextBoundaryMs - ts);
};

const EMPTY_DATA = {
  actual_schedule: [],
  actual_used_schedule: [],
  calculated_earned_labor_day: [],
  forecasted_labor_day: [],
  actual_used_labor_vs_earned_day: [],
  actual_intraday_metrics: {},
  daily_intraday_metrics: {},
  labor_violations: null,
};

export { getStoreNow };

/**
 * Self-contained Live Day Labor component that handles its own data fetching,
 * POS sync, and auto-refresh. Used by both the standalone tab and the modal.
 *
 * Props:
 *   storeId             - required
 *   startQhnum          - required (store's business day start quarter-hour)
 *   showActivityFeed    - show the activity alert feed
 *   hidePrint           - hide the print button
 *   storeName           - optional, shown in the header
 *   storeTimezone       - optional IANA timezone (e.g. "America/New_York")
 *   storeTeamMemberWeeklyInfo - optional, if provided skips the heavy weekly fetch
 *   businessWeekStartDay      - optional (e.g. "monday"), needed when fetching weekly info internally
 *   businessWeekStartDayIndex - optional (0-6), overrides day name when present
 *   refreshActualsForDate     - optional override callback
 */
function LiveDayLaborCore(props) {
  const {
    storeId,
    homeCompany,
    startQhnum,
    showActivityFeed,
    hidePrint,
    storeName,
    storeTimezone,
    storeTeamMemberWeeklyInfo: externalWeeklyInfo,
    businessWeekStartDay,
    businessWeekStartDayIndex,
    dailyPlanEnabled,
    onMissedShiftUpdated,
    kiosk = false,
    pinRequired = false,
  } = props;

  const [activeTab, setActiveTab] = useState("labor");
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(EMPTY_DATA);
  const [rawWeeklyInfo, setRawWeeklyInfo] = useState([]);
  // Per-store live-alert thresholds. Fetched once per store; the activity
  // feed reads each value as an override with the hard-coded literal as a
  // fallback, so an absent/default config reproduces current behavior.
  const [liveAlertConfig, setLiveAlertConfig] = useState(null);
  const lastQuarterRefreshMsRef = React.useRef(null);
  const activePosSyncContextRef = React.useRef(null);

  // Use store-relative day: if current hour is before start_qhnum,
  // we're still in the previous business day
  const storeNow = getStoreNow(storeTimezone);
  const now = dayjs(storeNow);
  const currentHour = now.hour() + now.minute() / 60;
  const todayStr =
    currentHour < startQhnum
      ? now.subtract(1, "day").format("YYYY-MM-DD")
      : now.format("YYYY-MM-DD");

  // Calculate the start of the current business week for weekly hours
  const dayNameToIndex = {
    sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
    thursday: 4, friday: 5, saturday: 6,
  };
  const parsedBusinessWeekStartDayIndex = Number(businessWeekStartDayIndex);
  const weekStartDayIndex = Number.isInteger(parsedBusinessWeekStartDayIndex) &&
    parsedBusinessWeekStartDayIndex >= 0 &&
    parsedBusinessWeekStartDayIndex <= 6
      ? parsedBusinessWeekStartDayIndex
      : dayNameToIndex[businessWeekStartDay?.toLowerCase()] ?? 1;
  const todayDate = dayjs(todayStr);
  const todayDayIndex = todayDate.day();
  const daysSinceWeekStart = (todayDayIndex - weekStartDayIndex + 7) % 7;
  const weekStartStr = todayDate
    .subtract(daysSinceWeekStart, "day")
    .format("YYYY-MM-DD");

  function agg_column(df, column) {
    return df?.reduce(function (a, b) {
      return a + b[column];
    }, 0);
  }

  // Queue the POS sync outside the web process and poll its lightweight status.
  // Non-fatal: errors are logged and we proceed to fetch stale data.
  const triggerPosSync = useCallback(async () => {
    const context = `${storeId}_${todayStr}`;
    activePosSyncContextRef.current = context;

    let refreshId = null;
    let pollDelayMs = POS_SYNC_INITIAL_POLL_DELAY_MS;

    while (activePosSyncContextRef.current === context) {
      try {
        const res = await postRightworkAPI(API_refresh_live_day_pos_data, {
          store_id: storeId,
          date: todayStr,
          ...(refreshId ? { refresh_id: refreshId } : {}),
        });
        const payload = res?.data?.redux?.payload || {};
        refreshId = payload.refresh_id || refreshId;

        if (!["queued", "running"].includes(payload.status)) {
          return payload.status;
        }
      } catch (error) {
        if (!isTransientPosSyncError(error)) {
          console.error("LiveDayLaborCore: POS sync failed", error);
          return "failed";
        }
        console.warn("LiveDayLaborCore: retrying POS sync poll", error);
      }

      await wait(pollDelayMs);
      pollDelayMs = Math.min(
        pollDelayMs * 2,
        POS_SYNC_MAX_POLL_DELAY_MS
      );
    }
    return "cancelled";
  }, [storeId, todayStr]);

  useEffect(() => {
    const context = `${storeId}_${todayStr}`;
    activePosSyncContextRef.current = context;
    return () => {
      if (activePosSyncContextRef.current === context) {
        activePosSyncContextRef.current = null;
      }
    };
  }, [storeId, todayStr]);

  const fetchData = useCallback(
    async (showLoading, syncFirst = false) => {
      if (syncFirst) {
        const syncStatus = await triggerPosSync();
        if (syncStatus === "cancelled") return;
      }
      if (showLoading) setIsLoading(true);
      const axiosConfig = getDefaultAxiosConfig();

      const buildUrl = (api) =>
        api + storeId + "&date_start=" + todayStr + "&date_end=" + todayStr;

      try {
        const [
          actualScheduleRes,
          actualUsedScheduleRes,
          calculatedEarnedLaborRes,
          actualUsedLaborRes,
          actualIntradayRes,
          intradayRes,
          forecastedLaborRes,
          liveLaborViolationsRes,
        ] = await Promise.all([
          axios.get(buildUrl(API_get_actual_schedule), axiosConfig),
          axios.get(buildUrl(API_get_actual_used_schedule), axiosConfig),
          axios.get(buildUrl(API_get_calculated_earned_labor), axiosConfig),
          axios.get(buildUrl(API_get_actual_used_labor_vs_earned), axiosConfig),
          axios.get(buildUrl(API_get_actual_intraday_metrics), axiosConfig),
          axios.get(buildUrl(API_get_intraday_metrics), axiosConfig),
          axios.get(buildUrl(API_get_forecasted_labor), axiosConfig),
          axios.get(API_get_live_labor_violations + storeId, axiosConfig).catch(() => ({ data: { violations: null } })),
        ]);

        // actual_schedule has redux.payload wrapper
        let actualSchedule = actualScheduleRes.data;
        if (actualSchedule?.redux?.payload?.actual_schedule_list) {
          actualSchedule = actualSchedule.redux.payload.actual_schedule_list;
        }

        // Live labor violations (based on actual worked shifts)
        const laborViolations = liveLaborViolationsRes.data?.violations || null;

        // actual_used_schedule can also be wrapped depending on endpoint/version
        let actualUsedSchedule = actualUsedScheduleRes.data;
        if (actualUsedSchedule?.redux?.payload?.actual_used_schedule_list) {
          actualUsedSchedule =
            actualUsedSchedule.redux.payload.actual_used_schedule_list;
        } else if (actualUsedSchedule?.redux?.payload?.actual_schedule_list) {
          actualUsedSchedule =
            actualUsedSchedule.redux.payload.actual_schedule_list;
        }

        // Intraday metrics are keyed by date
        const actualIntradayMetrics =
          actualIntradayRes.data?.[todayStr] || {};
        const dailyIntradayMetrics = intradayRes.data?.[todayStr] || {};

        // Labor data is flat arrays - filter to today
        const calculatedEarnedLabor = (
          calculatedEarnedLaborRes.data || []
        ).filter((d) => d.date === todayStr);
        const forecastedLabor = (forecastedLaborRes.data || []).filter(
          (d) => d.date === todayStr
        );
        const actualUsedLabor = (actualUsedLaborRes.data || []).filter(
          (d) => d.date === todayStr
        );

        // Used schedule is already filtered by date range from API
        actualUsedSchedule = actualUsedSchedule || [];

        // actual_schedule filtered to today
        const actualScheduleToday = (actualSchedule || []).filter(
          (d) => d.date === todayStr
        );

        setData({
          actual_schedule: actualScheduleToday,
          actual_used_schedule: actualUsedSchedule,
          calculated_earned_labor_day: calculatedEarnedLabor,
          forecasted_labor_day: forecastedLabor,
          actual_used_labor_vs_earned_day: actualUsedLabor,
          actual_intraday_metrics: actualIntradayMetrics,
          daily_intraday_metrics: dailyIntradayMetrics,
          labor_violations: laborViolations,
        });

      } catch (error) {
        console.error("LiveDayLaborCore: Error fetching data", error);
        setData((prev) =>
          prev === EMPTY_DATA || Object.keys(prev).length === 0
            ? EMPTY_DATA
            : prev
        );
      } finally {
        setIsLoading(false);
      }
    },
    [storeId, todayStr, triggerPosSync]
  );

  // Initial fetch: load existing data immediately, then sync POS
  // in the background and silently refresh once the sync completes.
  // The cancelled flag ensures a slow sync from a previous context
  // doesn't overwrite newer data after the user switches stores.
  const lastFetchContext = React.useRef(null);
  useEffect(() => {
    let cancelled = false;
    const context = `${storeId}_${todayStr}`;
    const isNewContext = lastFetchContext.current !== context;
    lastFetchContext.current = context;

    fetchData(isNewContext, false).then(() => {
      if (cancelled) return;
      triggerPosSync().then(() => {
        if (cancelled) return;
        fetchData(false, false);
      });
    });
    return () => {
      cancelled = true;
    };
  }, [fetchData, triggerPosSync, storeId, todayStr]);

  const refreshAtMostOncePerQuarter = useCallback(async () => {
    const quarterBoundaryMs = getCurrentQuarterBoundaryMs(Date.now());
    if (lastQuarterRefreshMsRef.current === quarterBoundaryMs) {
      return;
    }
    lastQuarterRefreshMsRef.current = quarterBoundaryMs;
    await fetchData(false, true);
  }, [fetchData]);

  const postMissedShift = useCallback(
    async (shiftId, missedShiftReason, options = {}) => {
      const requestData = {
        store_id: storeId,
        shift_id: shiftId,
        missed_shift_reason: missedShiftReason,
      };
      if (options.published_schedule_id) {
        requestData.published_schedule_id = options.published_schedule_id;
      }

      const res = await postRightworkAPI(API_post_missed_shift, requestData);
      await fetchData(false, false);
      await onMissedShiftUpdated?.();
      return res;
    },
    [fetchData, onMissedShiftUpdated, storeId]
  );

  // Auto-refresh aligned to :00 / :15 / :30 / :45 (with POS sync each cycle)
  useEffect(() => {
    let timeout = null;
    let cancelled = false;

    const scheduleNext = () => {
      if (cancelled) return;
      const delayMs = getMsUntilNextQuarterBoundary(Date.now());
      timeout = setTimeout(async () => {
        if (cancelled) return;
        await refreshAtMostOncePerQuarter();
        scheduleNext();
      }, delayMs);
    };

    scheduleNext();

    return () => {
      cancelled = true;
      if (timeout) clearTimeout(timeout);
    };
  }, [refreshAtMostOncePerQuarter]);

  // When returning to the app, catch up at most once for the current quarter
  // if the browser throttled timers in the background.
  useEffect(() => {
    const refreshOnForeground = () => {
      refreshAtMostOncePerQuarter();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshOnForeground();
      }
    };

    window.addEventListener("focus", refreshOnForeground);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.removeEventListener("focus", refreshOnForeground);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [refreshAtMostOncePerQuarter]);

  // Fetch weekly info internally only when not provided via props.
  // Retries with backoff on transient failures, then falls back to a
  // slow periodic retry so overtime/minor alerts can recover mid-session.
  const shouldFetchWeeklyInfo = !externalWeeklyInfo && !!storeId;
  useEffect(() => {
    if (!shouldFetchWeeklyInfo) return;

    let cancelled = false;
    let retryTimeout = null;
    const MAX_FAST_RETRIES = 3;
    const BASE_DELAY_MS = 5000;
    const SLOW_RETRY_MS = 60000;

    const attempt = (retryCount) => {
      const axiosConfig = getDefaultAxiosConfig();
      axios
        .post(
          API_get_store_team_member_weekly_info + storeId,
          { stores: [storeId], roles: [], date_start: weekStartStr },
          axiosConfig
        )
        .then((res) => {
          if (!cancelled) setRawWeeklyInfo(res.data || []);
        })
        .catch(() => {
          if (cancelled) return;
          if (retryCount < MAX_FAST_RETRIES) {
            const delay = BASE_DELAY_MS * Math.pow(2, retryCount);
            retryTimeout = setTimeout(() => attempt(retryCount + 1), delay);
          } else {
            retryTimeout = setTimeout(
              () => attempt(retryCount + 1),
              SLOW_RETRY_MS
            );
          }
        });
    };

    attempt(0);
    return () => {
      cancelled = true;
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, [storeId, weekStartStr, shouldFetchWeeklyInfo]);

  // Fetch the store's live-alert config once per store (rarely changes).
  useEffect(() => {
    if (!storeId) return;
    let cancelled = false;
    // Clear the previous store's config before fetching so the feed falls back
    // to baseline literals until this store's config resolves. Otherwise a slow
    // or failed fetch would apply the prior store's toggles/thresholds.
    setLiveAlertConfig(null);
    getRightworkAPI(API_get_live_alert_config, { store_id: storeId })
      .then((res) => {
        if (!cancelled) {
          setLiveAlertConfig(res?.data?.redux?.payload?.live_alert_config || null);
        }
      })
      .catch(() => {
        if (!cancelled) setLiveAlertConfig(null);
      });
    return () => {
      cancelled = true;
    };
  }, [storeId]);

  // Derive mapped weekly info by merging raw weekly data with is_minor
  // from the polled actual_used_schedule (updates each refresh cycle).
  const derivedWeeklyInfo = useMemo(() => {
    if (!shouldFetchWeeklyInfo) return [];

    const usedScheduleMinorMap = {};
    (data.actual_used_schedule || []).forEach((entry) => {
      if (
        entry.store_team_member?.id &&
        entry.store_team_member?.date_of_birth
      ) {
        const dob = new Date(entry.store_team_member.date_of_birth);
        const today = getStoreNow(storeTimezone);
        let age = today.getFullYear() - dob.getFullYear();
        const monthDiff = today.getMonth() - dob.getMonth();
        if (
          monthDiff < 0 ||
          (monthDiff === 0 && today.getDate() < dob.getDate())
        ) {
          age--;
        }
        usedScheduleMinorMap[entry.store_team_member.id] = age < 18;
      }
    });

    const todayDow = daysSinceWeekStart;
    return rawWeeklyInfo.map((member) => {
      let excludedHours = 0;
      for (let dow = todayDow; dow <= 6; dow++) {
        const dayShifts = member[String(dow)] || [];
        excludedHours += dayShifts.reduce(
          (sum, s) => sum + (s.hours || 0),
          0
        );
        const otherStoreShifts = member[`${dow}_other_store_shift`] || [];
        excludedHours += otherStoreShifts.reduce(
          (sum, s) => sum + (s.hours || 0),
          0
        );
      }
      return {
        ...member,
        id: member.store_team_member_id,
        hours:
          (member.hours || 0) +
          (member.other_store_hours || 0) -
          excludedHours,
        is_minor:
          usedScheduleMinorMap[member.store_team_member_id] ?? null,
      };
    });
  }, [
    rawWeeklyInfo,
    data.actual_used_schedule,
    daysSinceWeekStart,
    shouldFetchWeeklyInfo,
    storeTimezone,
  ]);

  // Use external weekly info if provided, otherwise use internally fetched data
  const storeTeamMemberWeeklyInfo = externalWeeklyInfo || derivedWeeklyInfo;

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <LoadSpinner />
      </div>
    );
  }

  return (
    <LiveDayLaborReportContent
      actual_schedule={data.actual_schedule}
      actual_used_schedule={data.actual_used_schedule}
      calculated_earned_labor_day={data.calculated_earned_labor_day}
      forecasted_labor_day={data.forecasted_labor_day}
      actual_used_labor_vs_earned_day={data.actual_used_labor_vs_earned_day}
      actual_intraday_metrics={data.actual_intraday_metrics}
      daily_intraday_metrics={data.daily_intraday_metrics}
      labor_violations={data.labor_violations}
      store_team_member_weekly_info={storeTeamMemberWeeklyInfo}
      agg_column={agg_column}
      date_str={todayStr}
      start_qhnum={startQhnum}
      store_id={storeId}
      store_name={storeName}
      liveAlertConfig={liveAlertConfig}
      hidePrint={hidePrint}
      showActivityFeed={showActivityFeed}
      post_missed_shift={postMissedShift}
      refreshActualsForDate={() => fetchData(false, true)}
      storeTimezone={storeTimezone}
      activeTab={dailyPlanEnabled ? activeTab : null}
      setActiveTab={dailyPlanEnabled ? setActiveTab : null}
      tasksPanel={
        dailyPlanEnabled ? (
          <LiveActionTasksPanel
            kiosk={kiosk}
            storeId={storeId}
            homeCompany={homeCompany}
            startQhnum={startQhnum}
            storeName={storeName}
            storeTimezone={storeTimezone}
            actualSchedule={data.actual_schedule}
            actualUsedSchedule={data.actual_used_schedule}
            pinRequired={pinRequired}
          />
        ) : null
      }
    />
  );
}

export default LiveDayLaborCore;
